/* eslint-disable cyclomatic-complexity/zee-codeBlockComplexity */
import fetch from 'cross-fetch';
import { AbortController } from 'node-abort-controller';

import {
  ctrackErrors,
  trackErrorEvent
} from 'src/utils/analytics/newrelic/RUMHelper';
import {
  deleteLocalStorage,
  getLocalStorage
} from 'src/utils/browserStorageHandler';
import { isServer } from 'src/utils/common';
import {
  HTTP_HEADER_KEYS,
  HTTP_METHODS
} from 'src/utils/constants/HttpMethodsConstants';
import {
  EXPIRES_IN,
  REFRESH_TOKEN,
  USER_TOKEN
} from 'src/utils/constants/localStorageConstants';
import {
  TOKEN_EXPIRED,
  TURN_OFF_VPN
} from 'src/utils/constants/stringConstants';

import APIService from './ApiService';
import { FetchAPIRequestParams } from './Types';
import { isTokenExpired } from '../apiRequests/Login/LoginService';
import { renewUserToken } from '../apiRequests/RenewTokenService';

export class HttpService {
  constructor() {
    this.isRenewCallPending = false;
    this.isRenewCallError = false;
    this.renewCount = 0;
    this.playerErrorCodes = [
      '3608',
      '606',
      '610',
      '3804',
      '601',
      '604',
      '3609',
      '3610',
      '3603',
      '3814',
      '603'
    ];
  }
  private static instance: HttpService;
  private isRenewCallPending: boolean;
  private isRenewCallError: boolean;
  private renewCount: number;
  private playerErrorCodes: string[];
  public static getInstance(): HttpService {
    if (!HttpService.instance) {
      HttpService.instance = new HttpService();
    }
    return HttpService.instance;
  }

  getDefaultTimeoutValue = () => {
    if (isServer()) {
      return 8 * 1000;
    }
    return 30 * 1000;
  };

  public async fetchApiData(
    params: FetchAPIRequestParams,
    retries = 3
  ): Promise<any> {
    const {
      url,
      type,
      body,
      headers = [],
      tempToken,
      timeout = this.getDefaultTimeoutValue(),
      customHeader,
      noAuthHeaderFlag = false
    } = params;
    let responseCode;
    //Required to bypass certificate check in network. Remove once kraken service is published publically
    // process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';
    const token: string = tempToken
      ? tempToken
      : (!isServer() && getLocalStorage(USER_TOKEN)) || '';
    const refreshToken: string = getLocalStorage(REFRESH_TOKEN) || '';
    const api = new APIService();
    if (
      token &&
      refreshToken &&
      isTokenExpired() &&
      !this.isRenewCallPending &&
      !this.isRenewCallError &&
      this.renewCount <= 3 &&
      retries > 0
    ) {
      this.isRenewCallPending = true;
      await renewUserToken(false, this.updateUserTokenRenewalApiCallStatus);
      return await this.fetchApiData({ url, type, body, headers }, retries - 1);
    } else if (
      token &&
      this.isRenewCallPending &&
      !this.isRenewCallError &&
      retries > 0
    ) {
      return await this.fetchApiData({ url, type, body, headers }, retries - 1);
    }

    api.setMethod(type);
    if (
      !noAuthHeaderFlag &&
      token &&
      !headers?.includes(HTTP_HEADER_KEYS.AUTHORIZATION)
    ) {
      headers?.push(HTTP_HEADER_KEYS.AUTHORIZATION);
    }
    if (type === HTTP_METHODS.POST) {
      api.setHeaders([HTTP_HEADER_KEYS.ACCEPT, HTTP_HEADER_KEYS.CONTENT_TYPE]);
    }
    if (Array.isArray(headers) && headers.length) {
      api.setHeaders(headers);
    }
    if (Array.isArray(customHeader) && customHeader.length) {
      api.setCustomHeaders(customHeader);
    }
    if (tempToken) {
      api.setTempToken(tempToken);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);
    const authApiErrorList = [471, 472, 473, 474, 475, 476];
    let response;
    let responseData;
    let errData;

    try {
      const res: any = await this.fetchCallGenerator(
        url,
        body,
        api,
        controller
      );

      response = res;
      responseData = await res.json();
      if (res.status !== 200) {
        errData = trackErrorEvent({
          eventName: 'API_FAILED_ERROR',
          url,
          responseStatus: response?.status,
          requestMethod: type,
          requestBody: body as { [key: string]: string },
          responseData
        });
      }
      responseCode = res.status;
      if (authApiErrorList.indexOf(res.status) > -1) {
        console.log(
          `Throwing error due to VPN error code for API ${url} on status ${responseCode}`
        );
        throw new Error(TURN_OFF_VPN);
      } else if (res.ok) {
        return responseData;
      } else {
        if (res.status === 401 || res.status === 400) {
          // player code handling
          const err = responseData;
          if (err && this.playerErrorCodes.includes(String(err.error_code))) {
            return err;
          } else if (
            token &&
            res.status === 401 &&
            String(err.error_code) == '401' &&
            err.error_msg == TOKEN_EXPIRED
          ) {
            deleteLocalStorage(USER_TOKEN);
            deleteLocalStorage(REFRESH_TOKEN);
            deleteLocalStorage(EXPIRES_IN);
            window.location.replace('/signin?redirectFromWebApp=1');
          }
          // refresh token handling
          else if (
            token &&
            res.status === 401 &&
            !this.isRenewCallPending &&
            !this.isRenewCallError &&
            this.renewCount <= 3 &&
            retries > 0
          ) {
            this.isRenewCallPending = true;
            await renewUserToken(
              false,
              this.updateUserTokenRenewalApiCallStatus
            );
            return await this.fetchApiData(
              { url, type, body, headers },
              retries - 1
            );
          } else if (res.status === 400) {
            /** RSJ flow when user already have active subscription and again give the
             * same number we will get 400 response.
             */
            return err;
          }
        }
        // everything else error handling
        else {
          return Promise.reject({
            url,
            res,
            errData,
            responseData
          });
        }
      }
    } catch (error) {
      console.error(
        `HTTP service error on ${url} with response ${responseCode} with message ${
          (error as Error).message
        }`
      );
      console.error(error);
      errData = trackErrorEvent({
        eventName: 'API_FAILED_ERROR',
        url,
        responseStatus: response?.status,
        requestMethod: type,
        requestBody: body as { [key: string]: string },
        responseData
      });
      ctrackErrors(error);
      if ((error as Error)?.message?.includes(TURN_OFF_VPN)) {
        throw new Error(TURN_OFF_VPN);
      } else {
        return Promise.reject({ url, responseData, errData });
      }
      //Bcz we were not throwing error in inner catch block, we were getting issue with location object.
      //Other solution is to initialize location obj by default value than to throw error here.
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  fetchCallGenerator = (
    url: string | URL,
    body: object | undefined,
    api: APIService,
    controller: any
  ) => {
    return (body && Object.keys(body).length) || body instanceof URLSearchParams
      ? fetch(url, { ...api.request(body), signal: controller.signal })
      : Object.keys(api.headers).length
      ? fetch(url, { ...api.requestHeaders(), signal: controller.signal })
      : fetch(url, { signal: controller.signal });
  };
  logApiError = async (error: any) => {
    let err;
    if (error && error.res) {
      if (error.responseData) {
        err = error.responseData;
      } else {
        err = await error.res.json();
      }
      if (err.code === 401) {
        deleteLocalStorage(USER_TOKEN);
        deleteLocalStorage(REFRESH_TOKEN);
        deleteLocalStorage(EXPIRES_IN);
      }
      ctrackErrors(err);
      return err;
    }
  };

  updateUserTokenRenewalApiCallStatus = (isRenewCallStatus: boolean) => {
    this.isRenewCallError = isRenewCallStatus ? isRenewCallStatus : false;
    this.renewCount += 1;
    this.isRenewCallPending = false;
  };
}

export const httpService = HttpService.getInstance();
